/**
 * IGA Entitlements wizard — assist for type / name / description.
 * Chips & aliases stay deterministic; unsure & unmatched free text → raw agent.
 */
import type { WizardQuickOption } from './vysorWizardEngine';
import {
  buildAgentContextHeader,
  isUnsureInput,
  shouldDelegateToAgent,
  DYNAMIC_AI_REPLY_RULES,
} from './vysorWizardAssistCommon';

export type IgaEntitlementAssistField = 'entitlement_type' | 'name' | 'description';

export type IgaEntitlementAssistOutcome =
  | { kind: 'pass' }
  | { kind: 'inferred'; fieldKey: IgaEntitlementAssistField; value: string; summary: string }
  | { kind: 'agent'; message: string; attachmentText?: string }
  | { kind: 'invalid'; hint: string };

const TYPE_KEYWORDS: Record<string, string[]> = {
  Role: ['role', 'job role', 'job function', 'rbac role', 'security role'],
  Permission: ['permission', 'privilege', 'capability', 'read-only', 'write access'],
  Group: ['group', 'ad group', 'distribution list', 'security group'],
  Access: ['access', 'access level', 'login access', 'application access'],
  Resource: ['resource', 'api resource', 'object', 'dataset', 'folder'],
};

const TYPE_ALIASES: Record<string, string> = {
  role: 'Role',
  permission: 'Permission',
  permissions: 'Permission',
  perm: 'Permission',
  group: 'Group',
  access: 'Access',
  resource: 'Resource',
};

function normalizeLookupKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function buildIgaEntitlementChoiceOptions(options: string[]): WizardQuickOption[] {
  return [
    ...options.map((o, i) => ({ id: `wz_opt_${i}`, label: o, value: o })),
    { id: 'wz_cancel', label: 'Cancel', value: '__cancel__' },
  ];
}

function matchTypeChoice(choiceOptions: string[], trimmed: string): string | undefined {
  if (!choiceOptions.length || !trimmed) return undefined;
  const exact = choiceOptions.find(o => o === trimmed || o.toLowerCase() === trimmed.toLowerCase());
  if (exact) return exact;
  const mapped = TYPE_ALIASES[normalizeLookupKey(trimmed)];
  if (mapped && choiceOptions.includes(mapped)) return mapped;
  return undefined;
}

function inferEntitlementType(text: string): { value: string; confidence: 'high' | 'medium' } | null {
  const lower = text.toLowerCase();
  let best: { key: string; score: number } | null = null;
  for (const [type, keys] of Object.entries(TYPE_KEYWORDS)) {
    const score = keys.filter(k => lower.includes(k)).length;
    if (score > 0 && (!best || score > best.score)) best = { key: type, score };
  }
  if (!best) return null;
  return { value: best.key, confidence: best.score >= 2 ? 'high' : 'medium' };
}

export function buildIgaEntitlementAgentContext(
  fieldKey: IgaEntitlementAssistField,
  collected: Record<string, string>,
  userMessage: string,
  attachmentText?: string,
): string {
  const lines = [
    `User is on IGA entitlements — field: ${fieldKey}.`,
    `Application: ${collected.application_name || 'unknown'} (ID: ${collected.application_id || 'pending'})`,
    `Environment: ${collected.instance_name || 'unknown'} (ID: ${collected.instance_id || 'pending'})`,
    collected.name ? `Draft entitlement name: ${collected.name}` : '',
    collected.description ? `Draft description: ${collected.description}` : '',
    '',
    DYNAMIC_AI_REPLY_RULES,
    fieldKey === 'entitlement_type'
      ? 'Recommend Role, Permission, Group, Access, or Resource when helpful.'
      : fieldKey === 'name'
        ? 'Suggest a clear entitlement name when helpful.'
        : 'Suggest a short description when helpful.',
    '',
    `User: ${userMessage}`,
  ].filter(Boolean);
  const header = buildAgentContextHeader('IGA entitlements wizard', lines);
  if (attachmentText?.trim()) {
    return `${header}\n[Reference material]\n${attachmentText.slice(0, 6000)}`;
  }
  return header;
}

export function resolveIgaEntitlementAssistInput(
  fieldKey: IgaEntitlementAssistField,
  rawAnswer: string,
  optionValue: string | undefined,
  collected: Record<string, string>,
  choiceOptions: string[] | undefined,
  assistActive: boolean,
): IgaEntitlementAssistOutcome {
  const trimmed = rawAnswer.trim();
  const options = choiceOptions || [];

  if (optionValue && (options.includes(optionValue) || optionValue === '__cancel__')) {
    return { kind: 'pass' };
  }

  if (isUnsureInput(trimmed)) {
    return {
      kind: 'agent',
      message: buildIgaEntitlementAgentContext(
        fieldKey,
        collected,
        trimmed || `I need help with the entitlement ${fieldKey}.`,
      ),
    };
  }

  if (fieldKey === 'name' || fieldKey === 'description') {
    if (shouldDelegateToAgent(trimmed, assistActive)) {
      return {
        kind: 'agent',
        message: buildIgaEntitlementAgentContext(fieldKey, collected, trimmed),
      };
    }
    if (!trimmed) {
      return {
        kind: 'invalid',
        hint:
          fieldKey === 'name'
            ? 'Enter an entitlement name, or ask in your own words.'
            : 'Enter a short description, or ask in your own words.',
      };
    }
    return { kind: 'pass' };
  }

  // entitlement_type
  if (options.length && matchTypeChoice(options, trimmed)) {
    return { kind: 'pass' };
  }

  const inferred = inferEntitlementType(trimmed);
  if (inferred && trimmed.length >= 15) {
    return {
      kind: 'inferred',
      fieldKey,
      value: inferred.value,
      summary: `From your description, this looks like a **${inferred.value}** entitlement.`,
    };
  }

  if (shouldDelegateToAgent(trimmed, assistActive) || trimmed) {
    return {
      kind: 'agent',
      message: buildIgaEntitlementAgentContext(fieldKey, collected, trimmed),
    };
  }

  return {
    kind: 'invalid',
    hint: 'Pick a type chip, or ask in your own words.',
  };
}
